import { useCallback } from 'react';

import type { SKUQuerySchema } from '@/_pages/settings/inventory-kits/shared/schemas/skus';
import { snackbar } from '@/_shared';

import { useCreateMutation, useUpdateMutation } from './shared';

export function useFormInputMultipleSKUs() {
  const { isPending, mutateAsync } = useCreateMutation();
  const { isPending: updateIsPending, mutateAsync: updateMutateSync } = useUpdateMutation();

  const handleAddSKU = useCallback(
    async (newV: SKUQuerySchema) => {
      try {
        const response = await mutateAsync({ sku: newV.sku });
        if (response.data) {
          return { ...newV, ...response.data };
        }
      } catch (error) {
        console.error(error);
        return false;
      }
      return false;
    },
    [mutateAsync]
  );

  const handleUpdateSKU = useCallback(
    async (sku: SKUQuerySchema) => {
      try {
        const response = await updateMutateSync({ sku: sku.sku, id: sku.id });
        if (response.data) {
          return { ...sku };
        }
      } catch (error) {
        console.error(error);
        return false;
      }
      return false;
    },
    [updateMutateSync]
  );

  const handleSKUs = useCallback(
    async (skus?: SKUQuerySchema[] | null) => {
      if (!skus || skus.length === 0) {
        return [];
      }
      const arrayOfSKUs: Promise<SKUQuerySchema | false>[] = [];
      skus.forEach(async (sku) => {
        if (sku.intent === 'create') {
          arrayOfSKUs.push(handleAddSKU(sku));
        } else if (sku.intent === 'update') {
          arrayOfSKUs.push(handleUpdateSKU(sku));
        } else {
          arrayOfSKUs.push(Promise.resolve(sku));
        }
      });

      const result = await Promise.all(arrayOfSKUs);

      if (result.some((r) => r === false)) {
        snackbar.error('Error while updating SKUs.');
        return false;
      }

      return result as SKUQuerySchema[];
    },
    [handleAddSKU, handleUpdateSKU]
  );

  return { handleSKUs, isSKUsPending: isPending || updateIsPending };
}
